/**
 * Orders Queue (§18 /queue, §30 priority). Read-only manager view — the
 * container that owns data + filter/paging/saved-view state; the table itself is
 * the presentational <OrderTable/>. Behaviour preserved:
 *  - server-sorted express-first (§30) — rows render in worker order, never
 *    re-sorted client-side;
 *  - status tabs with live counts from GET /orders/counts;
 *  - saved-view presets (§21) in localStorage;
 *  - offset pagination (page/limit);
 *  - empty/loading(skeleton)/error states; row click (or Enter) opens the
 *    nested OrderDrawer (<Outlet/>).
 * §A — multi-value filters (store · carrier · destination), a created-date range,
 * and Shopify-parity filters (financial status · payment gateway · shipping
 * method · tags) grouped under a Filters popover (Task 3). Filter menus are
 * data-driven from GET /orders/facets. Saved views capture the full predicate
 * set. Task 3 also adds CSV export + persisted column show/hide/reorder.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Outlet } from "react-router-dom";
import { STORE_REGIONS, type OrderStatus } from "@cairn/shared";
import { api, type OrderRow, type OrderCounts, type OrderFacets, type FacetValue } from "../lib/api.js";
import { type QueueFilter, filterToQuery, toViewFilter, activeFilterCount } from "../lib/filters.js";
import {
  type ColumnPrefs,
  loadColumnPrefs,
  saveColumnPrefs,
  normalizeColumnPrefs,
  visibleColumnKeys,
  toggleColumn,
  moveColumn,
} from "../lib/columnPrefs.js";
import { serializeCsv, downloadCsv } from "../lib/csv.js";
import {
  loadSavedViews,
  upsertSavedView,
  renameSavedView,
  deleteSavedView,
  type SavedView,
} from "../lib/savedViews.js";
import { statusLabel } from "../lib/status.js";
import { humanizeCode } from "../lib/format.js";
import { countryOptions } from "../lib/countries.js";
import { useSelection } from "../lib/useSelection.js";
import { modeLabel } from "../lib/batchStatus.js";
import { CreateBatchDialog, type CreateBatchOptions } from "./batches/CreateBatchDialog.js";
import { OrderTable, type QueueSort } from "./queue/OrderTable.js";
import { QUEUE_COLUMNS, QUEUE_COLUMN_KEYS, type QueueColumn } from "./queue/columns.js";
import { ColumnSettings } from "./queue/ColumnSettings.js";
import { SavedViews } from "./queue/SavedViews.js";
import { ScannerInput } from "../components/ScannerInput.js";
import {
  Tabs,
  type TabItem,
  Field,
  MultiSelect,
  DateRangeField,
  SearchBar,
  SelectionBar,
  Popover,
  Button,
  EmptyState,
  ErrorState,
  SkeletonRows,
  Icon,
  useToast,
} from "../components/ui/index.js";

const REQUIRED_COLUMN_KEYS: ReadonlySet<string> = new Set(
  QUEUE_COLUMNS.filter((c) => c.required).map((c) => c.key),
);

const COUNTRY_OPTIONS = countryOptions();

const DEFAULT_LIMIT = 50;

/** Status tabs (§18): the subset operators triage on day-to-day. */
const TAB_STATUSES = ["", "NEW", "ROUTED", "READY", "ON_HOLD", "EXCEPTION", "FULFILLED"];

/** Facet values → Select options (humanized label, raw value), count-annotated. */
function facetOptions(values: FacetValue[] | undefined, humanize = false) {
  return (values ?? []).map((f) => ({
    value: f.value,
    label: `${humanize ? humanizeCode(f.value) : f.value} (${f.count})`,
  }));
}

export function Queue() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { toast } = useToast();

  // --- filter + paging state (§A: all predicates are multi-value) ---
  const [status, setStatus] = useState<string[]>([]); // tab-driven (0 or 1), saved views may carry several
  const [stores, setStores] = useState<string[]>([]);
  const [carriers, setCarriers] = useState<string[]>([]);
  const [dests, setDests] = useState<string[]>([]);
  const [financial, setFinancial] = useState<string[]>([]);
  const [gateways, setGateways] = useState<string[]>([]);
  const [methods, setMethods] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [createdFrom, setCreatedFrom] = useState<string | undefined>(undefined);
  const [createdTo, setCreatedTo] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string>(""); // Task 1 — transient free-text find
  const [sort, setSort] = useState<QueueSort | null>(null); // Task 2 — null = express-first
  const [page, setPage] = useState<number>(1);
  const limit = DEFAULT_LIMIT;

  // --- Task 3 column preferences (show/hide + order), persisted per device ---
  const [colPrefs, setColPrefs] = useState<ColumnPrefs>(() =>
    typeof window === "undefined"
      ? normalizeColumnPrefs(null, QUEUE_COLUMN_KEYS)
      : loadColumnPrefs(window.localStorage, QUEUE_COLUMN_KEYS),
  );
  const [exporting, setExporting] = useState(false);

  // --- data ---
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [counts, setCounts] = useState<OrderCounts | null>(null);
  const [facets, setFacets] = useState<OrderFacets | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // --- Task 4 bulk selection + batch creation ---
  const selection = useSelection();
  const clearSelection = selection.clear; // stable (useCallback) — safe as a hook dep
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // --- saved views (§21, localStorage) ---
  const [views, setViews] = useState<SavedView[]>(() =>
    typeof window === "undefined" ? [] : loadSavedViews(window.localStorage),
  );

  const filter = useMemo<QueueFilter>(
    () => ({
      status,
      storeId: stores,
      carrierCode: carriers,
      destinationCountry: dests,
      financialStatus: financial,
      paymentGateway: gateways,
      shippingMethod: methods,
      tags,
      createdFrom,
      createdTo,
      search,
      sort: sort?.key,
      dir: sort?.dir,
      page,
      limit,
    }),
    [status, stores, carriers, dests, financial, gateways, methods, tags, createdFrom, createdTo, search, sort, page, limit],
  );
  const activeViewFilter = useMemo(() => toViewFilter(filter), [filter]);
  const filterCount = activeFilterCount(activeViewFilter);

  // Task 3 — the ordered, visible columns resolved from the persisted prefs.
  const visibleColumns = useMemo<QueueColumn[]>(() => {
    const byKey = new Map(QUEUE_COLUMNS.map((c) => [c.key, c]));
    return visibleColumnKeys(colPrefs, REQUIRED_COLUMN_KEYS)
      .map((k) => byKey.get(k))
      .filter((c): c is QueueColumn => !!c);
  }, [colPrefs]);

  const updateColPrefs = useCallback((next: ColumnPrefs) => {
    setColPrefs(next);
    try {
      saveColumnPrefs(window.localStorage, next);
    } catch {
      /* private mode — keep in-memory only */
    }
  }, []);

  // Facets are global (all orders), fetched once. Failure → empty menus, not fatal.
  useEffect(() => {
    let live = true;
    api
      .getOrderFacets()
      .then((f) => live && setFacets(f))
      .catch(() => live && setFacets(null));
    return () => {
      live = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = filterToQuery(filter);
      // Tab counts reflect every OTHER active filter, across all statuses.
      const countQuery = filterToQuery({ ...filter, status: [], page: undefined });
      const [pageRes, countsRes] = await Promise.all([
        api.getOrders(query),
        api.getOrderCounts(countQuery).catch(() => null),
      ]);
      setOrders(pageRes.orders);
      setTotal(pageRes.total);
      setHasNext(pageRes.hasNext);
      setCounts(countsRes);
    } catch {
      setOrders([]);
      setTotal(0);
      setHasNext(false);
      setCounts(null);
      setError("Couldn’t load the queue. Retry in a moment.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  // Bulk selection is scoped to the current view — reset it when the query
  // (predicate / search / sort / page) changes so the count never lies.
  useEffect(() => {
    clearSelection();
  }, [filter, clearSelection]);

  // §12.1/§15/§40 (Tasks 2-3) — create a batch from the selected orders with the
  // chosen mode + document options, then (label-first) optionally run the carrier
  // labels up front, and open the batch detail. The batched orders move to
  // IN_BATCH and drop out of the open tabs.
  const createBatch = useCallback(
    async (opts: CreateBatchOptions) => {
      const orderIds = selection.ids;
      if (orderIds.length === 0) return;
      setCreatingBatch(true);
      try {
        const { batch } = await api.createBatch({ orderIds, mode: opts.mode, note: opts.note || undefined });
        // §40.2 — label-first: generate every order's label in one batch run (mock carrier).
        if (opts.mode === "LABEL_FIRST" && opts.runLabels) {
          await api.printBatchLabels(batch.id).catch(() => undefined);
        }
        toast(
          `Batch created · ${orderIds.length} order${orderIds.length === 1 ? "" : "s"} · ${modeLabel(opts.mode)}`,
          "success",
        );
        selection.clear();
        setShowCreateDialog(false);
        navigate(`/batches/${batch.id}`);
      } catch {
        toast("Couldn’t create the batch. Retry in a moment.", "danger");
      } finally {
        setCreatingBatch(false);
      }
    },
    [selection, toast, navigate],
  );

  // Task 3 — export the current filtered/sorted result set (all pages, capped) as
  // a CSV of exactly the visible columns, in their current order.
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const CAP = 5000;
      const all: OrderRow[] = [];
      for (let p = 1; ; p += 1) {
        const res = await api.getOrders(filterToQuery({ ...filter, page: p, limit: 200 }));
        all.push(...res.orders);
        if (!res.hasNext || all.length >= CAP) break;
      }
      const ctx = { now: Date.now() };
      const headers = visibleColumns.map((c) => c.header);
      const rows = all.map((o) => visibleColumns.map((c) => c.text(o, ctx)));
      downloadCsv(`cairn-queue-${new Date().toISOString().slice(0, 10)}.csv`, serializeCsv(headers, rows));
      toast(`Exported ${all.length} order${all.length === 1 ? "" : "s"}`, "success");
    } catch {
      toast("Couldn’t export. Retry in a moment.", "danger");
    } finally {
      setExporting(false);
    }
  }, [filter, visibleColumns, toast]);

  // Reset to page 1 whenever a predicate changes.
  const applyPredicate = useCallback((fn: () => void) => {
    setPage(1);
    fn();
  }, []);

  const selectView = useCallback(
    (v: SavedView) => {
      applyPredicate(() => {
        setStatus(v.filter.status ?? []);
        setStores(v.filter.storeId ?? []);
        setCarriers(v.filter.carrierCode ?? []);
        setDests(v.filter.destinationCountry ?? []);
        setFinancial(v.filter.financialStatus ?? []);
        setGateways(v.filter.paymentGateway ?? []);
        setMethods(v.filter.shippingMethod ?? []);
        setTags(v.filter.tags ?? []);
        setCreatedFrom(v.filter.createdFrom);
        setCreatedTo(v.filter.createdTo);
      });
    },
    [applyPredicate],
  );

  const clearAll = useCallback(() => {
    applyPredicate(() => {
      setStatus([]);
      setStores([]);
      setCarriers([]);
      setDests([]);
      setFinancial([]);
      setGateways([]);
      setMethods([]);
      setTags([]);
      setCreatedFrom(undefined);
      setCreatedTo(undefined);
    });
  }, [applyPredicate]);

  const saveView = useCallback(
    (name: string) => {
      setViews(upsertSavedView(window.localStorage, name, activeViewFilter));
      toast(`Saved view “${name}”`, "success");
    },
    [activeViewFilter, toast],
  );

  const renameView = useCallback((id: string, name: string) => {
    setViews(renameSavedView(window.localStorage, id, name));
  }, []);

  const removeView = useCallback((id: string) => {
    setViews(deleteSavedView(window.localStorage, id));
  }, []);

  // Task 2 — click a header to sort (server-side); click again toggles direction.
  // Express-first remains the default until a sort is chosen.
  const onSort = useCallback(
    (key: string) => {
      applyPredicate(() =>
        setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" })),
      );
    },
    [applyPredicate],
  );

  const now = Date.now();

  // Carrier options: prefer the global facet set; fall back to the current page.
  const carrierOptions = useMemo(() => {
    if (facets?.carrierCode?.length) return facetOptions(facets.carrierCode, true);
    const seen = Array.from(new Set(orders.map((o) => o.carrierCode).filter((c): c is string => !!c))).sort();
    return seen.map((c) => ({ value: c, label: humanizeCode(c) }));
  }, [facets, orders]);

  const tabs = useMemo<TabItem[]>(
    () =>
      TAB_STATUSES.map((id) => ({
        id,
        label: id === "" ? "All" : statusLabel(id),
        count: id === "" ? counts?.total : counts?.byStatus[id as OrderStatus],
      })),
    [counts],
  );
  // The tabs are single-status; a multi-status saved view highlights no tab.
  const activeTab = status.length === 1 ? status[0]! : "";

  return (
    <section className="queue">
      {/* Task 1 — global scan-to-pack: scanning an order's packing-slip barcode
          (off any bench) jumps straight to packing it. Suspended while the drawer
          is open so its focus trap is undisturbed. */}
      <ScannerInput disabled={!!orderId} onScan={(code) => navigate(`/pack?order=${encodeURIComponent(code)}`)} />
      <div className="page-head">
        <div className="page-head__title">
          <h1>Queue</h1>
          <span className="page-head__count">
            {total} order{total === 1 ? "" : "s"}
          </span>
        </div>
        <Tabs items={tabs} value={activeTab} onChange={(id) => applyPredicate(() => setStatus(id ? [id] : []))} ariaLabel="Order status" />
      </div>

      {/* Toolbar (Task 3) — a Filters control grouping every filter, the prominent
          multi-field search (Task 1), and right-aligned actions: Export (CSV of
          the current view) and Column settings (persisted). Status lives in the
          tabs above; filter menus are data-driven from /orders/facets. */}
      <div className="toolbar">
        <Popover label="Filters" icon="filter" badge={filterCount} panelLabel="Filters" panelClassName="filters-panel">
          <div className="filters-panel__grid">
            <Field label="Store">
              <MultiSelect
                ariaLabel="Filter by store"
                placeholder="All stores"
                summaryNoun="stores"
                value={stores}
                onChange={(v) => applyPredicate(() => setStores(v))}
                options={STORE_REGIONS.map((r) => ({ value: r, label: r }))}
              />
            </Field>
            <Field label="Carrier">
              <MultiSelect
                ariaLabel="Filter by carrier"
                placeholder="All carriers"
                summaryNoun="carriers"
                value={carriers}
                onChange={(v) => applyPredicate(() => setCarriers(v))}
                options={carrierOptions}
              />
            </Field>
            <Field label="Destination">
              <MultiSelect
                searchable
                ariaLabel="Filter by destination country"
                placeholder="Any destination"
                summaryNoun="countries"
                searchPlaceholder="Country (e.g. DE)"
                value={dests}
                onChange={(v) => applyPredicate(() => setDests(v))}
                options={COUNTRY_OPTIONS}
              />
            </Field>
            <Field label="Created">
              <DateRangeField
                from={createdFrom}
                to={createdTo}
                onChange={(f, t) => applyPredicate(() => {
                  setCreatedFrom(f);
                  setCreatedTo(t);
                })}
              />
            </Field>
            <Field label="Financial status">
              <MultiSelect
                ariaLabel="Filter by financial status"
                placeholder="Any status"
                summaryNoun="statuses"
                value={financial}
                onChange={(v) => applyPredicate(() => setFinancial(v))}
                options={facetOptions(facets?.financialStatus, true)}
              />
            </Field>
            <Field label="Payment gateway">
              <MultiSelect
                ariaLabel="Filter by payment gateway"
                placeholder="Any gateway"
                summaryNoun="gateways"
                value={gateways}
                onChange={(v) => applyPredicate(() => setGateways(v))}
                options={facetOptions(facets?.paymentGateway, true)}
              />
            </Field>
            <Field label="Shipping method">
              <MultiSelect
                searchable
                ariaLabel="Filter by shipping method"
                placeholder="Any method"
                summaryNoun="methods"
                searchPlaceholder="Method title"
                value={methods}
                onChange={(v) => applyPredicate(() => setMethods(v))}
                options={facetOptions(facets?.shippingMethod)}
              />
            </Field>
            <Field label="Tags">
              <MultiSelect
                searchable
                ariaLabel="Filter by order tags"
                placeholder="Any tag"
                summaryNoun="tags"
                searchPlaceholder="Tag"
                value={tags}
                onChange={(v) => applyPredicate(() => setTags(v))}
                options={facetOptions(facets?.tags)}
              />
            </Field>
          </div>
          <div className="filters-panel__footer">
            <span className="filters-panel__count">{filterCount === 0 ? "No filters applied" : `${filterCount} filter${filterCount === 1 ? "" : "s"} active`}</span>
            {filterCount > 0 && (
              <Button variant="ghost" size="sm" icon="x" onClick={clearAll}>
                Clear all
              </Button>
            )}
          </div>
        </Popover>

        <SearchBar
          className="toolbar__search"
          ariaLabel="Search orders"
          placeholder="Search order #, customer, destination, SKU, tracking…"
          focusShortcut="/"
          value={search}
          onChange={(v) => applyPredicate(() => setSearch(v))}
        />

        <div className="toolbar__actions">
          <Button variant="secondary" size="sm" icon="download" disabled={exporting || orders.length === 0} onClick={() => void exportCsv()}>
            {exporting ? "Exporting…" : "Export"}
          </Button>
          <Popover label="Columns" icon="columns" align="right" panelLabel="Column settings" panelClassName="col-settings-popover">
            <ColumnSettings
              columns={QUEUE_COLUMNS}
              prefs={colPrefs}
              requiredKeys={REQUIRED_COLUMN_KEYS}
              onToggle={(k) => updateColPrefs(toggleColumn(colPrefs, k, REQUIRED_COLUMN_KEYS))}
              onMove={(k, d) => updateColPrefs(moveColumn(colPrefs, k, d))}
              onReset={() => updateColPrefs(normalizeColumnPrefs(null, QUEUE_COLUMN_KEYS))}
            />
          </Popover>
        </div>
      </div>

      {/* Saved views (§21). */}
      <SavedViews
        views={views}
        activeFilter={activeViewFilter}
        onApply={selectView}
        onSave={saveView}
        onRename={renameView}
        onDelete={removeView}
      />

      {/* Table — server order; do NOT re-sort (§30). */}
      {error ? (
        <ErrorState title="Couldn’t load the queue" hint="The worker may be busy. Retry in a moment." onRetry={() => void load()} />
      ) : loading && orders.length === 0 ? (
        <div className="table-card">
          <SkeletonRows rows={10} cols={visibleColumns.length + 1} />
        </div>
      ) : orders.length === 0 ? (
        <EmptyState
          title="No orders in this view"
          hint="Nothing matches the current filters. Clear a filter or switch tabs."
        />
      ) : (
        <OrderTable
          orders={orders}
          columns={visibleColumns}
          selectedId={orderId}
          onOpen={(id) => navigate(`/queue/${id}`)}
          onPack={(id) => navigate(`/pack?order=${encodeURIComponent(id)}`)}
          sort={sort}
          onSort={onSort}
          selection={selection}
          now={now}
        />
      )}

      {/* Pagination (§17). Shown when there are rows OR we're past page 1, so an
          accidentally-empty page > 1 still exposes an enabled Prev. */}
      {(orders.length > 0 || page > 1) && (
        <footer className="pager">
          <Button
            variant="secondary"
            size="sm"
            icon="chevronLeft"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <span className="pager__info">
            Page {page}
            {total > 0
              ? ` · ${Math.min((page - 1) * limit + 1, total)}–${Math.min(page * limit, total)} of ${total}`
              : ""}
          </span>
          <Button variant="secondary" size="sm" disabled={!hasNext || loading} onClick={() => setPage((p) => p + 1)}>
            Next
            <Icon name="chevronRight" />
          </Button>
        </footer>
      )}

      {/* Task 4 — bulk-select action bar (floats; appears when rows are selected). */}
      <SelectionBar count={selection.count} onClear={selection.clear}>
        <Button variant="primary" size="sm" icon="layers" disabled={creatingBatch} onClick={() => setShowCreateDialog(true)}>
          Create batch
        </Button>
      </SelectionBar>

      {showCreateDialog && (
        <CreateBatchDialog
          count={selection.count}
          busy={creatingBatch}
          onConfirm={(opts) => void createBatch(opts)}
          onCancel={() => setShowCreateDialog(false)}
        />
      )}

      {/* Drawer (nested /queue/:orderId). */}
      <Outlet />
    </section>
  );
}
